import React, { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { particleVertex, particleFragment } from './shaders';
import { LOBE_COLORS } from './anatomy';
import { useSimStore, computeAnticip } from '../state/store';

const C = {
  frontal: new THREE.Color(LOBE_COLORS.frontal),
  parietal: new THREE.Color(LOBE_COLORS.parietal),
  temporal: new THREE.Color(LOBE_COLORS.temporal),
  occipital: new THREE.Color(LOBE_COLORS.occipital),
  limbic: new THREE.Color(LOBE_COLORS.limbic),
  generic: new THREE.Color(LOBE_COLORS.generic),
  red: new THREE.Color('#ff3b30'),
  gold: new THREE.Color('#ffd700'),
  crimson: new THREE.Color('#8b0000'),
};

// A single brain's neural web. Driven entirely by the shared store, so every
// instance stays perfectly synchronised (Hemisphere Sync).
export default function NeuralWeb({ positions, regions, seeds, synapses, clipPlanes = null, compareEvent = null }) {
  const matRef = useRef();
  const synMatRef = useRef();

  const uniforms = useMemo(
    () => ({
      uTime: { value: 0 },
      uAnticip: { value: 0 },
      uEvent: { value: 0 },
      uEventTime: { value: 0 },
      uHover: { value: 0 },
      uSize: { value: 19 },
      uPixelRatio: { value: Math.min(2, typeof window !== 'undefined' ? window.devicePixelRatio : 1) },
      uWinBoost: { value: 0 },
      uFrontal: { value: C.frontal },
      uParietal: { value: C.parietal },
      uTemporal: { value: C.temporal },
      uOccipital: { value: C.occipital },
      uLimbic: { value: C.limbic },
      uGeneric: { value: C.generic },
      uRed: { value: C.red },
      uGold: { value: C.gold },
      uCrimson: { value: C.crimson },
    }),
    []
  );

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    const s = useSimStore.getState();
    const anticip = computeAnticip(s.wager, s.parlay);
    let eventCode, et, winBoost;
    if (compareEvent) {
      // Held, looping comparison response (Win on one brain, Loss on the other).
      // Kept contained so both brains stay readable side-by-side.
      const now = performance.now() / 1000;
      eventCode = compareEvent === 'win' ? 1 : compareEvent === 'loss' ? 2 : 0;
      et = compareEvent === 'win' ? (now % 1.7) * 0.68 : now % 1.4;
      winBoost = 0;
    } else {
      eventCode = s.event === 'win' ? 1 : s.event === 'loss' ? 2 : s.event === 'clutch' ? 3 : 0;
      et = s.event === 'none' ? 0 : performance.now() / 1000 - s.eventStart;
      winBoost = s.event === 'win' && s.streakType === 'win' && s.streakCount >= 3 ? 1 : 0;
    }

    if (matRef.current) {
      const u = matRef.current.uniforms;
      u.uTime.value = t;
      u.uAnticip.value = compareEvent ? 0 : anticip;
      u.uEvent.value = eventCode;
      u.uEventTime.value = et;
      u.uHover.value = compareEvent ? 0 : s.hoveredRegion;
      u.uWinBoost.value = winBoost;
    }
    if (synMatRef.current) {
      synMatRef.current.opacity = Math.max(0, 0.1 + Math.sin(t * 2.2) * 0.05 + anticip * 0.14);
    }
  });

  return (
    <group>
      <points frustumCulled={false}>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" args={[positions, 3]} />
          <bufferAttribute attach="attributes-aRegion" args={[regions, 1]} />
          <bufferAttribute attach="attributes-aSeed" args={[seeds, 1]} />
        </bufferGeometry>
        <shaderMaterial
          ref={matRef}
          uniforms={uniforms}
          vertexShader={particleVertex}
          fragmentShader={particleFragment}
          transparent
          depthWrite={false}
          blending={THREE.NormalBlending}
          clipping={!!clipPlanes}
          clippingPlanes={clipPlanes || []}
        />
      </points>

      <lineSegments frustumCulled={false}>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" args={[synapses, 3]} />
        </bufferGeometry>
        <lineBasicMaterial
          ref={synMatRef}
          transparent
          opacity={0.12}
          color={'#8aa0b4'}
          blending={THREE.NormalBlending}
          depthWrite={false}
        />
      </lineSegments>
    </group>
  );
}
